import { CreatorInsightsView } from "@features/aiScriptGeneration/ui/CreatorInsightsView";
import { Modal } from "@shared/ui";
import { useEffect, useRef } from "react";
import formStyles from "./conceptForm.module.scss";
import { ConceptPicker } from "./ConceptPicker";
import styles from "./editor.module.scss";
import { ScriptEditor, ScriptEditorHandle } from "./ScriptEditor";
import { ScriptResultCard } from "./ScriptResultCard";
import { ScriptFlowHeader, scriptFlowStep } from "./ScriptFlowHeader";
import { ScriptJobNotice } from "./ScriptJobNotice";
import { ScriptOutputFields } from "./ScriptOutputFields";
import { holdScriptGeneration } from "./finishedScript";
import { isSchemaScriptHtml } from "./scriptDocument";
import { ScriptDraft } from "./types";
import { useQueuedScriptGenerate } from "./useQueuedScriptGenerate";
import { useScriptConceptStage } from "./useScriptConceptStage";
import { useScriptGenerationSession } from "./useScriptGenerationSession";

export function ScriptGenerationEditor({
  active,
  aiWorkspaceId,
  onBack,
  onChange,
  onDownload,
  isDownloading,
  onGeneratingChange,
}: {
  active: ScriptDraft;
  aiWorkspaceId: string;
  onBack: () => void;
  onChange: (
    patch: Partial<
      Pick<ScriptDraft, "brief" | "settings" | "markdown" | "status">
    >
  ) => void;
  onDownload: () => void;
  isDownloading?: boolean;
  onGeneratingChange?: (generating: boolean) => void;
}) {
  const editorRef = useRef<ScriptEditorHandle>(null);
  const session = useScriptGenerationSession(active, editorRef, onChange, onGeneratingChange);
  const stage = useScriptConceptStage(
    active,
    session.job,
    onChange,
    session.refetch
  );
  const showWorkspace = !session.analyzing && !session.failed;
  const showScript = Boolean(session.job && showWorkspace && stage.showScript);
  const queue = useQueuedScriptGenerate(editorRef, {
    jobId: active.creator.jobId,
    showScript,
    generating: session.generating,
    generate: session.generate,
  });
  const writingScript = stage.showScript || session.generating;
  const working = session.generating || stage.busy;

  useEffect(() => {
    if (working) holdScriptGeneration(true);
    else if (!session.generating) holdScriptGeneration(false);
    onGeneratingChange?.(working);
  }, [onGeneratingChange, session.generating, working]);
  const conceptsRef = useRef<HTMLDivElement>(null);
  const scriptSectionRef = useRef<HTMLDivElement>(null);
  const shouldScrollToScriptRef = useRef(false);

  const scrollToConcepts = () => {
    window.setTimeout(() => {
      conceptsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleGenerateConcepts = () => {
    if (!stage.hasConcepts) {
      stage.regenerate();
      scrollToConcepts();
    } else {
      stage.regenerate();
    }
  };

  useEffect(() => {
    if (showScript && shouldScrollToScriptRef.current) {
      shouldScrollToScriptRef.current = false;
      window.setTimeout(() => {
        scriptSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 50);
    }
  }, [showScript]);

  const scrollToScript = () => {
    if (!showScript) {
      shouldScrollToScriptRef.current = true;
    } else {
      window.setTimeout(() => {
        scriptSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 50);
    }
  };

  return (
    <div className={styles.editorShell}>
      <div className={styles.editorLayout}>
        <div className={styles.editorMain}>
          <ScriptFlowHeader
            creatorName={active.creator.name}
            current={scriptFlowStep({
              analyzing: session.analyzing,
              failed: session.failed,
              hasAnalysis: Boolean(session.contentAnalysis),
              writingScript,
            })}
            scriptReady={session.outputReady && Boolean(active.markdown.trim())}
            onBack={onBack}
          />
          {session.analyzing ? (
            <ScriptJobNotice tone="loading" title="Analyzing creator profile" />
          ) : null}
          {session.failed ? (
            <ScriptJobNotice
              tone="error"
              title="This script job failed"
              detail="The script could not be prepared for this creator."
            />
          ) : null}
          {showWorkspace && session.contentAnalysis ? (
            <section className={formStyles.readOnly}>
              <div className={formStyles.readOnlyHead}>
                <h3>Content style</h3>
                {/* <span className={formStyles.badge}>Read only</span> */}
              </div>
              <CreatorInsightsView
                analysis={session.contentAnalysis}
                showContentStyleHeading={false}
              />
            </section>
          ) : null}
          {showWorkspace ? (
            <>
              <ScriptOutputFields
                active={active}
                aiWorkspaceId={aiWorkspaceId}
                generating={stage.busy || session.generating}
                hint="The brief is optional. Output settings stay on their defaults until you open Advanced."
                actionLabel={
                  stage.busy
                    ? "Drafting concepts"
                    : stage.hasConcepts
                      ? "Regenerate concepts"
                      : "Generate concepts"
                }
                onChange={(patch) => {
                  if (patch.settings) {
                    stage.changeSettings(patch.settings);
                    return;
                  }
                  onChange(patch);
                }}
                onBriefBlur={stage.commitBrief}
                onGenerate={handleGenerateConcepts}
              />
              <div ref={conceptsRef}>
                {stage.busy ? (
                  <ScriptJobNotice
                    tone="loading"
                    title="Drafting concepts"
                    detail="Using the current brief and output settings."
                  />
                ) : (
                  <ConceptPicker
                    concepts={stage.concepts}
                    generating={false}
                    writingId={
                      session.generating ? session.selectedConceptId : ""
                    }
                    selectedId={session.selectedConceptId}
                    onSelect={(concept) => {
                      stage.openScript();
                      queue.request(
                        concept._id,
                        session.selectedConceptId,
                        active.markdown || session.scriptSeed
                      );
                      scrollToScript();
                    }}
                  />
                )}
              </div>
            </>
          ) : null}
          {showScript ? (
            <ScriptResultCard
              cardRef={scriptSectionRef}
              conceptsRef={conceptsRef}
              showConcepts={stage.concepts.length > 0}
              saved={session.outputReady && Boolean(active.markdown.trim())}
              canDownload={Boolean(active.markdown.trim())}
              canEdit={session.canEdit}
              isDownloading={isDownloading}
              onDownload={onDownload}
            >
              <ScriptEditor
                key={active.creator.jobId}
                ref={editorRef}
                documentId={active.id}
                seed={
                  isSchemaScriptHtml(active.markdown)
                    ? active.markdown
                    : session.scriptSeed
                }
                loadKey={session.loadKey}
                disabled={!session.canEdit}
                generating={session.generating}
                canRevise={session.canEdit}
                jobId={active.creator.jobId}
                onMarkdownChange={(markdown) => onChange({ markdown })}
              />
            </ScriptResultCard>
          ) : null}
        </div>
      </div>
      <Modal
        open={stage.confirmOpen}
        title="Regenerate concepts?"
        okText="Regenerate"
        cancelText="Cancel"
        onOk={() => {
          stage.acceptRegenerate();
          scrollToConcepts();
        }}
        onCancel={stage.cancelRegenerate}
      >
        Changing the brief or output settings replaces the current concepts.
      </Modal>
    </div>
  );
}
